<template>
  <div
    v-if="showAvailabilityStatus"
    :class="[
      'availability-status',
      getAvailabilityStatus.class,
      theme ? `availability-status--${theme}` : null,
      hideStatusText ? 'availability-status--without-status-text' : null,
      alignRight ? 'availability-status--right' : null,
    ]"
  >
    <tooltip-fi
      :class="['availability-status__tooltip', customTooltipClass]"
      :tooltip="getAvailabilityStatus.text"
      :custom-class="getAvailabilityStatus.class"
    >
      <template #trigger>
        <span :class="['availability-status__trigger', getAvailabilityStatus.class]" />
      </template>
    </tooltip-fi>
    <span
      v-if="!hideStatusText"
      class="availability-status__text"
      data-test="availability-status"
      :data-test-2="getAvailabilityStatus.speed"
      :data-test-3="getAvailabilityStatus.date"
    >
      {{ getAvailabilityStatus.text }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import TooltipFi from '@/components/atoms/tooltip-fi/TooltipFi.vue';
import { useDate } from '@/composables/date';
import { useDict } from '@/composables/fischerPlugin';
import {
  isSeefelderDeliveryOnRequest,
  useSeefelderDelivery,
} from '@/composables/seefelderDelivery';

export interface StockLevel {
  warehouse?: {
    code?: string | null;
  } | null;
}

interface Props {
  deliverySpeed?: string | null;
  deliveryDate?: string | null;
  disableTooltip?: boolean;
  customTooltipClass?: string;
  id?: string;
  theme?: string;
  hideStatusText?: boolean;
  showSmallIndicatorDot?: boolean;
  stockLevels?: (null | StockLevel)[] | null;
  alignRight?: boolean;
  isSeefelder: boolean;
  articleNumber?: string | null;
  requestedQuantity?: number;
}

const props = withDefaults(defineProps<Props>(), {
  deliverySpeed: 'NODELIVERY',
  deliveryDate: null,
  disableTooltip: false,
  customTooltipClass: '',
  id: '',
  theme: '',
  hideStatusText: false,
  showSmallIndicatorDot: false,
  stockLevels: null,
  alignRight: false,
  articleNumber: null,
  requestedQuantity: 1,
});

const dict = useDict();
const { formatDate } = useDate();

const { delivery: seefelderDelivery, hasSettled: seefelderHasSettled } = useSeefelderDelivery(
  () => props.articleNumber,
  () => props.requestedQuantity,
  () => props.isSeefelder,
);

interface AvailabilityStatusView {
  class: string;
  text: string;
  speed: string | null;
  date: string | null;
}

const EMPTY_STATUS: AvailabilityStatusView = {
  class: '',
  text: '',
  speed: null,
  date: null,
};

const seefelderStatus = (
  key: string,
  replacements: string[] | undefined,
  view: Pick<AvailabilityStatusView, 'class' | 'speed'>,
): AvailabilityStatusView | null => {
  const text = dict.get(key, replacements, { noDevFallback: true });
  return text ? { ...view, text, date: null } : null;
};

const resolvedAvailabilityStatus = computed<AvailabilityStatusView | null>(() => {
  if (props.isSeefelder) {
    if (!seefelderHasSettled.value) {
      return null;
    }

    const delivery = seefelderDelivery.value;
    const days = delivery?.deliveryTimeInWorkingDays;

    if (!delivery || typeof days !== 'number' || isSeefelderDeliveryOnRequest(delivery)) {
      return seefelderStatus('online-Shop.Product.Delivery-Seefelder-On-Request', undefined, {
        class: 'availability-status--slow-delivery',
        speed: 'SLOWDELIVERY',
      });
    }

    return seefelderStatus('online-Shop.Product.Delivery-Seefelder-Days', [String(days)], {
      class: 'availability-status--fast-delivery',
      speed: 'FASTDELIVERY',
    });
  }

  const stockWithCode = props.stockLevels?.find((stockLevel) => stockLevel?.warehouse?.code);
  const code = stockWithCode?.warehouse?.code;

  const deliverySpeed =
    (code &&
      dict.get(`online-Shop.Product.Warehouse.${code}-${props.deliverySpeed}-Speed`, undefined, {
        noDevFallback: true,
      })) ||
    props.deliverySpeed;
  const warehouseText =
    code &&
    dict.get(`online-Shop.Product.Warehouse.${code}-${props.deliverySpeed}`, undefined, {
      noDevFallback: true,
    });

  const deliveryDate = props.deliveryDate ? formatDate(props.deliveryDate) : null;

  const isFastDelivery = deliverySpeed === 'FASTDELIVERY';
  const isSlowDelivery = !deliverySpeed || deliverySpeed === 'SLOWDELIVERY';
  let availabilityText = dict.get('online-Shop.Product.No-Delivery');
  let availabilityClass = 'availability-status--no-delivery';

  if (isFastDelivery) {
    availabilityText = dict.get('online-Shop.Product.Fast-Delivery');
    availabilityClass = 'availability-status--fast-delivery';
  } else if (isSlowDelivery) {
    const isFutureDeliveryDate = props.deliveryDate && new Date(props.deliveryDate) > new Date();
    if (deliveryDate && isFutureDeliveryDate) {
      availabilityText = dict.get('online-Shop.Product.Slow-Delivery-With-Date', [deliveryDate]);
    } else {
      availabilityText = dict.get('online-Shop.Product.Slow-Delivery');
    }
    availabilityClass = 'availability-status--slow-delivery';
  }

  return {
    class: availabilityClass,
    text: warehouseText || availabilityText,
    speed: deliverySpeed,
    date: deliveryDate,
  };
});

const showAvailabilityStatus = computed(() => resolvedAvailabilityStatus.value !== null);
const getAvailabilityStatus = computed(() => resolvedAvailabilityStatus.value ?? EMPTY_STATUS);
</script>

<style scoped src="./availability-status" />
